import { modalName } from '@va/standalone/shared/constants';

import { apiStatus, flagKeys } from '@va/constants';
import { put, select, take } from 'redux-saga/effects';

import { openModal } from '@va/dashboard/actions/ui';
import { getAccountInformation, getAnnouncementsSelector } from '@va/dashboard/selectors/api';

import * as Types from '@va/standalone/shared/actions';
import { GET_AGENCY_CUSTOMER_SUCCEEDED } from '@va/standalone/shared/actions';
import { INTERN_ACCOUNT_EMAIL_ADDRESS } from '@va/standalone/shared/constants';
import { isUserLoggedInWithCustomUrl } from '@va/standalone/shared/helpers';
import * as Selectors from '@va/standalone/shared/selectors';
import { getApiRequest, getApiRequestStatus, getLastCreatedSubscription } from '@va/standalone/shared/selectors';
import { AccountInformationType, BusinessInfoType } from '@va/types/account';
import { SubscriptionStatus, UserRole } from '@va/types/website';
import { AccountDataType, getAnnouncementsToBeDisplayed, isDevelopFeatureEnabled } from '@va/util/helpers';
import dayjs from 'dayjs';
import { Map } from 'immutable';

import { modalName as commonModalName } from '@va/constants';

import { setAnnouncementData } from '@va/dashboard/actions/ui';
import { getRequestStatus } from '@va/dashboard/selectors/api';

import { hasUnlimitedVoucher as hasUnlimitedVoucherSelector } from '@va/dashboard/selectors/app';
import { activeWebsiteSelectors } from '@va/dashboard/selectors/core';
import { ActiveWebsiteSliceData } from '@va/dashboard/slices';
import { ActiveSubscriptionType } from '@va/standalone/shared/types';
import { AnnouncementType } from '@va/types/announcements';
import { WixPermissionPack } from '@va/wix/shared/constants';
import { getPermissionPackage } from '@va/wix/shared/selectors';

const FIRST_SHORT_BUSINESS_INFO_MODAL_INTERVAL_DAYS = 7;

export function* handleShortBusinessInfoModal() {
  const accountInfo: AccountInformationType & {
    status: apiStatus;
  } = yield select(getAccountInformation);

  const agencyCustomerInfoStatus: apiStatus = yield select((state) => getApiRequestStatus(state, 'getAgencyCustomer'));
  if (accountInfo?.hasCustomer && agencyCustomerInfoStatus !== apiStatus.SUCCEEDED)
    yield take([GET_AGENCY_CUSTOMER_SUCCEEDED]);

  const agencyCustomerInfoMap: Map<string, any> = yield select((state) => getApiRequest(state, 'getAgencyCustomer'));
  const agencyCustomerInfo = agencyCustomerInfoMap.toJS() as BusinessInfoType & { status: apiStatus };

  const doNotShowAgain = accountInfo.frontendFlags?.[flagKeys.DO_NOT_SHOW_BUSINESS_INFO_MODAL];

  const isInfoComplete = !!(
    agencyCustomerInfo.companyName &&
    agencyCustomerInfo.businessSector &&
    agencyCustomerInfo.businessType &&
    agencyCustomerInfo.businessSize
  );

  const isOpen =
    accountInfo.status === apiStatus.SUCCEEDED &&
    agencyCustomerInfo.status !== apiStatus.IN_PROGRESS &&
    agencyCustomerInfo.status !== apiStatus.FAILED &&
    !isInfoComplete &&
    !doNotShowAgain &&
    dayjs().diff(dayjs.unix(accountInfo.createdDate), 'days') >= FIRST_SHORT_BUSINESS_INFO_MODAL_INTERVAL_DAYS;

  if (isOpen) {
    yield put(openModal(modalName.SHORT_BUSINESS_INFO));
  }
}

export function* handleAnnouncementModalSaga() {
  const accountInformation: ReturnType<typeof getAccountInformation> = yield select(getAccountInformation);
  const activeWebsite: ActiveWebsiteSliceData = yield select(activeWebsiteSelectors.selectData);
  const isActiveWebsiteInitialized: boolean = yield select(activeWebsiteSelectors.isInitialized);

  const hasUnlimitedVoucher: boolean = yield select(hasUnlimitedVoucherSelector);
  const isWixClaimed: boolean = yield select(Selectors.isWixClaimed);
  const wixPermissionPack: WixPermissionPack = yield select(getPermissionPackage);

  const isOnCustomDomain = isUserLoggedInWithCustomUrl();

  const requestStatus: apiStatus = yield select((state) => getRequestStatus(state, 'getAnnouncements'));

  if (requestStatus === apiStatus.IN_PROGRESS) {
    yield take('GET_ANNOUNCEMENTS_SUCCEEDED');
  }

  const announcements: AnnouncementType[] = yield select(getAnnouncementsSelector) ?? [];

  // const announcements = dummyAnnouncements; // For manual testing, can be left here for now

  const subscriptionStatus: apiStatus = yield select(getApiRequestStatus, 'getAllSubscriptions');
  let hasPremiumSubscription = false;

  if (isWixClaimed) {
    hasPremiumSubscription = wixPermissionPack > WixPermissionPack.FREE;
  } else {
    if (subscriptionStatus !== apiStatus.SUCCEEDED && subscriptionStatus !== apiStatus.FAILED) {
      yield take([
        Types.standalone.Api.GET_ALL_SUBSCRIPTIONS_SUCCEEDED,
        Types.standalone.Api.GET_ALL_SUBSCRIPTIONS_FAILED,
      ]);

      const subscription: ActiveSubscriptionType = yield select(getLastCreatedSubscription);
      hasPremiumSubscription = subscription && subscription.status === SubscriptionStatus.ACTIVE;
    }
  }

  if (!accountInformation || !isActiveWebsiteInitialized || !announcements) return;

  const isWebsiteOwner = activeWebsite.extra.role === UserRole.OWNER;

  const { isAgencyOwner, frontendFlags, email } = accountInformation ?? {};
  const { numberOfMonthlyVisits, ipCountryISO, installDate, platform } = activeWebsite.core ?? {};

  const lastAccountBasedModalViewedAt = frontendFlags?.[flagKeys.LAST_ACCOUNT_BASED_ANNOUNCEMENT_VIEWED_AT] as
    | number
    | undefined;

  const accountData: AccountDataType = {
    isOnCustomDomain,
    isAgencyOwner,
    frontendFlags: frontendFlags ?? {},
    lastAccountBasedModalViewedAt,
    isInternalUser: email === INTERN_ACCOUNT_EMAIL_ADDRESS || isDevelopFeatureEnabled(),
    creationDate: accountInformation.createdDate,
  };

  const websiteData =
    Object.keys(activeWebsite).length === 0
      ? undefined
      : {
          numberOfMonthlyVisits,
          ipCountryISO,
          installDate,
          isWebsiteOwner,
          platform,
          hasPremiumSubscription,
          hasUnlimitedVoucher: hasUnlimitedVoucher || activeWebsite.extra.unlimitedPlanActive,
        };

  const announcementsToBeDisplayed = getAnnouncementsToBeDisplayed(announcements, accountData, websiteData);

  yield put(setAnnouncementData(announcementsToBeDisplayed));
  if (announcementsToBeDisplayed.length !== 0) {
    yield put(openModal(commonModalName.announcement));
  }
}
